<script setup lang="ts">
import { Search, X } from 'lucide-vue-next'

import AppDatePicker from '@/components/AppDatePicker.vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

withDefaults(
  defineProps<{
    search: string
    dateFrom: string
    dateTo: string
    hasActiveFilters: boolean
    searchPlaceholder?: string
    datePrefixLabel?: string
  }>(),
  {
    searchPlaceholder: 'Search…',
    datePrefixLabel: 'Date:',
  },
)

const emit = defineEmits<{
  'update:search': [value: string]
  'update:dateFrom': [value: string]
  'update:dateTo': [value: string]
  clear: []
}>()

const today = new Date().toISOString().split('T')[0]
</script>

<template>
  <div class="border-border border-b px-4 pb-4">
    <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      <!-- Search -->
      <div class="relative min-w-48 flex-1">
        <Search
          class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        />
        <Input
          :value="search"
          :placeholder="searchPlaceholder"
          class="pl-9"
          @input="emit('update:search', ($event.target as HTMLInputElement).value)"
        />
      </div>

      <!-- Page-specific filters (selects, etc.) -->
      <slot />

      <!-- Date range -->
      <div class="flex w-full flex-col items-center gap-1.5 sm:flex-row md:w-120">
        <span class="text-muted-foreground hidden shrink-0 text-xs md:block">{{ datePrefixLabel }}</span>
        <AppDatePicker
          :model-value="dateFrom"
          :max="dateTo || today"
          placeholder="From date"
          class="w-full sm:w-40"
          @update:model-value="emit('update:dateFrom', $event)"
        />
        <span class="text-muted-foreground hidden text-xs select-none md:block">–</span>
        <AppDatePicker
          :model-value="dateTo"
          :min="dateFrom || undefined"
          :max="today"
          placeholder="To date"
          class="w-full sm:w-40"
          @update:model-value="emit('update:dateTo', $event)"
        />
      </div>

      <!-- Clear all -->
      <Button
        v-if="hasActiveFilters"
        variant="ghost"
        size="sm"
        class="text-muted-foreground hover:text-foreground shrink-0"
        @click="emit('clear')"
      >
        <X class="size-4" />
        Clear
      </Button>
    </div>
  </div>
</template>
